import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  MdCheckCircle,
  MdWarning,
  MdError,
  MdRefresh,
  MdHistory,
  MdSend,
  MdClose,
  MdSchedule,
  MdVerifiedUser,
  MdExpandMore,
  MdExpandLess,
  MdDescription,
} from "react-icons/md";
import toast from "react-hot-toast";
import * as Styled from "./styles";
import { useAuth } from "shared/providers/auth";
import {
  getPatientReferralStatus,
  updateReferralStatus,
  resendReferral,
  getReferralHistory,
  verifyReferralStatus,
  verifyAllPendingReferrals,
} from "requests/ReferralTracking";
import {
  IReferralFormWithTracking,
  IHistoryItem,
  ReferralStatus,
} from "shared/types/ReferralTracking";

interface IProps {
  pacienteId: number;
  onUpdate?: () => void;
  onClose?: () => void;
}

type TabType = "pendentes" | "expirados" | "realizados";

const ReferralStatusTracker: React.FC<IProps> = ({ pacienteId, onUpdate, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<TabType>("pendentes");
  const [pendentes, setPendentes] = React.useState<IReferralFormWithTracking[]>(
    []
  );
  const [expirados, setExpirados] = React.useState<IReferralFormWithTracking[]>(
    []
  );
  const [realizados, setRealizados] = React.useState<
    IReferralFormWithTracking[]
  >([]);
  const [totais, setTotais] = React.useState({
    pendentes: 0,
    expirados: 0,
    realizados: 0,
  });

  // Dialog states
  const [showHistoryDialog, setShowHistoryDialog] = React.useState(false);
  const [showMarkDoneDialog, setShowMarkDoneDialog] = React.useState(false);
  const [selectedForm, setSelectedForm] =
    React.useState<IReferralFormWithTracking | null>(null);
  const [historyData, setHistoryData] = React.useState<IHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [markDoneObservacao, setMarkDoneObservacao] = React.useState("");
  const [actionLoading, setActionLoading] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState<Set<number>>(new Set());

  const loadData = React.useCallback(async () => {
    if (!pacienteId) return;

    setLoading(true);
    try {
      const { status, data } = await getPatientReferralStatus(
        pacienteId,
        user.data.id
      );

      if ([200, 201].includes(status) && data.success) {
        setPendentes(data.data.pendentes || []);
        setExpirados(data.data.expirados || []);
        setRealizados(data.data.realizados || []);
        setTotais(data.data.totais);
      }
    } catch (error) {
      console.error("Erro ao carregar status:", error);
    } finally {
      setLoading(false);
    }
  }, [pacienteId, user.data.id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleVerifyAll = async () => {
    setActionLoading(true);
    try {
      const { status, data } = await verifyAllPendingReferrals(
        pacienteId,
        user.data.id
      );

      if ([200, 201].includes(status)) {
        if (data.data.total_atualizados > 0) {
          toast.success(
            `${data.data.total_atualizados} formulário(s) atualizado(s)!`
          );
          loadData();
          onUpdate?.();
        } else {
          toast.success("Nenhum formulário foi atualizado automaticamente.");
        }
      }
    } catch (error) {
      toast.error("Erro ao verificar formulários");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsDone = async () => {
    if (!selectedForm) return;

    setActionLoading(true);
    try {
      const { status, data } = await updateReferralStatus(
        selectedForm.id,
        { status: "realizado", observacao: markDoneObservacao || undefined },
        user.data.id
      );

      if ([200, 201].includes(status)) {
        toast.success("Marcado como realizado!");
        setShowMarkDoneDialog(false);
        setSelectedForm(null);
        setMarkDoneObservacao("");
        loadData();
        onUpdate?.();
      } else {
        toast.error(data.message || "Erro ao atualizar status");
      }
    } catch (error) {
      toast.error("Erro ao marcar como realizado");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async (form: IReferralFormWithTracking) => {
    setActionLoading(true);
    try {
      const { status, data } = await resendReferral(form.id, user.data.id);

      if ([200, 201].includes(status)) {
        toast.success(
          `Reenviado com sucesso! (Tentativa ${data.data.tentativa})`
        );
        loadData();
        onUpdate?.();
      } else {
        toast.error(data.message || "Erro ao reenviar");
      }
    } catch (error) {
      toast.error("Erro ao reenviar formulário");
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowHistory = async (form: IReferralFormWithTracking) => {
    setSelectedForm(form);
    setShowHistoryDialog(true);
    setHistoryLoading(true);

    try {
      const { status, data } = await getReferralHistory(form.id, user.data.id);

      if ([200, 201].includes(status)) {
        setHistoryData(data.data.historico || []);
      }
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleVerifySingle = async (form: IReferralFormWithTracking) => {
    setActionLoading(true);
    try {
      const { status, data } = await verifyReferralStatus(
        form.id,
        user.data.id,
        true
      );

      if ([200, 201].includes(status)) {
        if (data.data.verificacao.status_atualizado) {
          toast.success("Status atualizado automaticamente!");
          loadData();
          onUpdate?.();
        } else {
          toast.success(data.data.verificacao.motivo);
        }
      }
    } catch (error) {
      toast.error("Erro ao verificar status");
    } finally {
      setActionLoading(false);
    }
  };

  const openMarkDoneDialog = (form: IReferralFormWithTracking) => {
    setSelectedForm(form);
    setShowMarkDoneDialog(true);
    setMarkDoneObservacao("");
  };

  const toggleExpanded = (id: number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderFormDetails = (item: IReferralFormWithTracking) => {
    const info = item.informacoes_principais;
    
    switch (item.tipo) {
      case 'encaminhamento':
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> Encaminhamento
            </Styled.DetailRow>
            {info.destinatario && (
              <Styled.DetailRow>
                <strong>Destinatário:</strong> {info.destinatario}
              </Styled.DetailRow>
            )}
            {info.descricao && (
              <Styled.DetailRow>
                <strong>Para realização de:</strong> {info.descricao}
              </Styled.DetailRow>
            )}
            {info.solicita_laudo && (
              <Styled.DetailRow>
                <strong>Solicita laudo:</strong> {info.solicita_laudo}
              </Styled.DetailRow>
            )}
            {info.data_observacao && (
              <Styled.DetailRow>
                <strong>Retornar em:</strong> {info.data_observacao}
              </Styled.DetailRow>
            )}
          </Styled.DetailsContent>
        );

      case 'declaracao_tratamentos':
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> Declaração de Tratamentos Complementares
            </Styled.DetailRow>
            {info.responsavel && (
              <Styled.DetailRow>
                <strong>Responsável:</strong> {info.responsavel}
              </Styled.DetailRow>
            )}
            {info.tratamentos && (
              <Styled.DetailRow>
                <strong>Tratamentos:</strong> {info.tratamentos}
              </Styled.DetailRow>
            )}
            {info.outros && (
              <Styled.DetailRow>
                <strong>Outros:</strong> {info.outros}
              </Styled.DetailRow>
            )}
          </Styled.DetailsContent>
        );

      case 'receituario':
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> Receituário
            </Styled.DetailRow>
            {info.para && (
              <Styled.DetailRow>
                <strong>Para:</strong> {info.para}
              </Styled.DetailRow>
            )}
            {info.numero && (
              <Styled.DetailRow>
                <strong>Nº Notificação:</strong> {info.numero}
              </Styled.DetailRow>
            )}
            {info.uso && (
              <Styled.DetailRow>
                <strong>Uso:</strong> {info.uso}
              </Styled.DetailRow>
            )}
          </Styled.DetailsContent>
        );

      case 'termo_conduta':
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> Termo de Conduta
            </Styled.DetailRow>
            {(info.retorno_dias || info.retorno_semanas) && (
              <Styled.DetailRow>
                <strong>Retorno em:</strong>{" "}
                {info.retorno_dias ? `${info.retorno_dias} dia(s)` : ""}
                {info.retorno_dias && info.retorno_semanas ? " e " : ""}
                {info.retorno_semanas ? `${info.retorno_semanas} semana(s)` : ""}
              </Styled.DetailRow>
            )}
          </Styled.DetailsContent>
        );

      case 'indicacao_cirurgica':
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> Ciência de Indicação Cirúrgica
            </Styled.DetailRow>
            {info.responsavel && (
              <Styled.DetailRow>
                <strong>Responsável:</strong> {info.responsavel}
              </Styled.DetailRow>
            )}
            {info.cpf_responsavel && (
              <Styled.DetailRow>
                <strong>CPF:</strong> {info.cpf_responsavel}
              </Styled.DetailRow>
            )}
            {info.rg_responsavel && (
              <Styled.DetailRow>
                <strong>RG:</strong> {info.rg_responsavel}
              </Styled.DetailRow>
            )}
          </Styled.DetailsContent>
        );

      default:
        return (
          <Styled.DetailsContent>
            <Styled.DetailRow>
              <strong>Tipo:</strong> {item.tipo_descricao}
            </Styled.DetailRow>
          </Styled.DetailsContent>
        );
    }
  };

  const getStatusIcon = (status: ReferralStatus) => {
    switch (status) {
      case "realizado":
        return <MdCheckCircle color="#28a745" size={18} />;
      case "expirado":
        return <MdError color="#dc3545" size={18} />;
      case "pendente":
        return <MdSchedule color="#ffc107" size={18} />;
      default:
        return <MdWarning color="#6c757d" size={18} />;
    }
  };

  const getItemsList = () => {
    switch (activeTab) {
      case "pendentes":
        return pendentes;
      case "expirados":
        return expirados;
      case "realizados":
        return realizados;
      default:
        return [];
    }
  };

  const renderItem = (item: IReferralFormWithTracking) => {
    const isExpanded = expandedItems.has(item.id);
    
    return (
      <Styled.ItemCard key={item.id} status={item.status}>
        <Styled.ItemHeader>
          <Styled.ItemTitle>
            <h4>{item.tipo_descricao}</h4>
            <span>Criado em: {item.data_criacao}</span>
            {item.tentativa > 1 && <span> • Tentativa {item.tentativa}</span>}
          </Styled.ItemTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Styled.StatusBadge status={item.status}>
              {getStatusIcon(item.status)}
              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </Styled.StatusBadge>
            <Tooltip title={isExpanded ? "Recolher detalhes" : "Ver detalhes"}>
              <IconButton 
                size="small" 
                onClick={() => toggleExpanded(item.id)}
                sx={{ padding: "4px" }}
              >
                {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Styled.ItemHeader>

        {item.alerta && (
          <Styled.AlertBadge tipo={item.alerta.tipo}>
            {item.alerta.tipo === "danger" && <MdError size={16} />}
            {item.alerta.tipo === "warning" && <MdWarning size={16} />}
            {item.alerta.mensagem}
          </Styled.AlertBadge>
        )}

        {/* Resumo básico sempre visível */}
        <Styled.ItemInfo>
          {item.informacoes_principais.procedimentos && (
            <p>
              <strong>Procedimentos:</strong>{" "}
              {item.informacoes_principais.procedimentos}
            </p>
          )}
          {item.informacoes_principais.tratamentos && (
            <p>
              <strong>Tratamentos:</strong>{" "}
              {item.informacoes_principais.tratamentos}
            </p>
          )}
          {item.informacoes_principais.descricao && (
            <p>
              <strong>Descrição:</strong> {item.informacoes_principais.descricao}
            </p>
          )}
          {item.data_vencimento && (
            <p>
              <strong>Vencimento:</strong> {item.data_vencimento}
              {item.dias_ate_vencimento !== null && (
                <span>
                  {" "}
                  ({item.dias_ate_vencimento > 0
                    ? `${item.dias_ate_vencimento} dias restantes`
                    : `${Math.abs(item.dias_ate_vencimento)} dias em atraso`}
                  )
                </span>
              )}
            </p>
          )}
        </Styled.ItemInfo>

        {/* Detalhes expandidos */}
        {isExpanded && (
          <Styled.ExpandedDetails>
            <Styled.DetailsHeader>
              <MdDescription size={16} />
              Detalhes do Formulário
            </Styled.DetailsHeader>
            {renderFormDetails(item)}
          </Styled.ExpandedDetails>
        )}

        <Styled.Actions>
          {(item.status === "pendente" || item.status === "expirado") && (
            <>
              <Tooltip title="Marcar como realizado">
                <Styled.ActionButton
                  variant="success"
                  onClick={() => openMarkDoneDialog(item)}
                  disabled={actionLoading}
                >
                  <MdCheckCircle size={14} />
                  Realizado
                </Styled.ActionButton>
              </Tooltip>

              <Tooltip title="Verificar automaticamente">
                <Styled.ActionButton
                  variant="primary"
                  onClick={() => handleVerifySingle(item)}
                  disabled={actionLoading}
                >
                  <MdVerifiedUser size={14} />
                  Verificar
                </Styled.ActionButton>
              </Tooltip>
            </>
          )}

          {item.status === "expirado" && (
            <Tooltip title="Reenviar encaminhamento">
              <Styled.ActionButton
                variant="danger"
                onClick={() => handleResend(item)}
                disabled={actionLoading}
              >
                <MdSend size={14} />
                Reenviar
              </Styled.ActionButton>
            </Tooltip>
          )}

          <Tooltip title="Ver histórico">
            <Styled.ActionButton
              variant="outline"
              onClick={() => handleShowHistory(item)}
            >
              <MdHistory size={14} />
              Histórico
            </Styled.ActionButton>
          </Tooltip>
        </Styled.Actions>
      </Styled.ItemCard>
    );
  };

  if (loading) {
    return (
      <Styled.Container>
        <Styled.LoadingContainer>
          <CircularProgress />
        </Styled.LoadingContainer>
      </Styled.Container>
    );
  }

  return (
    <Styled.Container>
      <Styled.Header>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h3>Encaminhamentos</h3>
          <Styled.Badges>
            {totais.pendentes > 0 && (
              <Styled.Badge variant="pendente">
                {totais.pendentes} pendente{totais.pendentes > 1 ? "s" : ""}
              </Styled.Badge>
            )}
            {totais.expirados > 0 && (
              <Styled.Badge variant="expirado">
                {totais.expirados} expirado{totais.expirados > 1 ? "s" : ""}
              </Styled.Badge>
            )}
          </Styled.Badges>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small">
            <MdClose size={24} />
          </IconButton>
        )}
      </Styled.Header>

      <Styled.Content>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
            gap: 1,
          }}
        >
          <Tooltip title="Atualizar lista">
            <IconButton onClick={loadData} size="small">
              <MdRefresh />
            </IconButton>
          </Tooltip>
          {totais.pendentes > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<MdVerifiedUser />}
              onClick={handleVerifyAll}
              disabled={actionLoading}
            >
              Verificar Todos
            </Button>
          )}
        </Box>

        <Styled.TabsContainer>
          <Styled.Tab
            active={activeTab === "pendentes"}
            onClick={() => setActiveTab("pendentes")}
          >
            Pendentes ({totais.pendentes})
          </Styled.Tab>
          <Styled.Tab
            active={activeTab === "expirados"}
            onClick={() => setActiveTab("expirados")}
          >
            Expirados ({totais.expirados})
          </Styled.Tab>
          <Styled.Tab
            active={activeTab === "realizados"}
            onClick={() => setActiveTab("realizados")}
          >
            Realizados ({totais.realizados})
          </Styled.Tab>
        </Styled.TabsContainer>

        {getItemsList().length === 0 ? (
          <Styled.EmptyState>
            {activeTab === "pendentes" && <MdSchedule size={48} />}
            {activeTab === "expirados" && <MdError size={48} />}
            {activeTab === "realizados" && <MdCheckCircle size={48} />}
            <p>
              {activeTab === "pendentes" && "Nenhum encaminhamento pendente"}
              {activeTab === "expirados" && "Nenhum encaminhamento expirado"}
              {activeTab === "realizados" && "Nenhum encaminhamento realizado"}
            </p>
          </Styled.EmptyState>
        ) : (
          getItemsList().map(renderItem)
        )}
      </Styled.Content>

      {/* Dialog de Marcar como Realizado */}
      <Dialog
        open={showMarkDoneDialog}
        onClose={() => setShowMarkDoneDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Marcar como Realizado
          <IconButton
            onClick={() => setShowMarkDoneDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <p style={{ marginBottom: 16 }}>
              Confirma que o encaminhamento{" "}
              <strong>{selectedForm?.tipo_descricao}</strong> foi realizada?
            </p>
            <TextField
              label="Observação (opcional)"
              multiline
              rows={3}
              fullWidth
              value={markDoneObservacao}
              onChange={(e) => setMarkDoneObservacao(e.target.value)}
              placeholder="Ex: Procedimento realizado em clínica externa..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMarkDoneDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkAsDone}
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <MdCheckCircle />
              )
            }
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Histórico */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Histórico de Tentativas
          <IconButton
            onClick={() => setShowHistoryDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Styled.HistoryTimeline>
              {historyData.map((item, index) => (
                <Styled.HistoryItem key={item.id} status={item.status}>
                  <Styled.HistoryContent>
                    <h5>
                      Tentativa {item.tentativa} - {item.status_descricao}
                    </h5>
                    <span>{item.data_criacao}</span>
                    {item.observacao_realizado && (
                      <p>Observação: {item.observacao_realizado}</p>
                    )}
                    {item.data_realizado && (
                      <p>Realizado em: {item.data_realizado}</p>
                    )}
                  </Styled.HistoryContent>
                </Styled.HistoryItem>
              ))}
            </Styled.HistoryTimeline>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Styled.Container>
  );
};

export { ReferralStatusTracker };
